import { api, ApiError } from "utils/api";
import { useQueryClient, useMutation } from "react-query";
import { useForm } from "react-hook-form";
import type { UploadedImage } from "features/images";
import { Artist, ArtistFormFields } from "../types";
import { artistPlaceHolder } from "./useArtist";

/**
 * Merge Artist form fields with defaults from placeholder data
 */
const withDefaults = (formData: ArtistFormFields) => ({
  ...artistPlaceHolder(formData.id),
  ...formData,
  type: "ARTIST",
  status: "PUBLISHED",
});

/**
 * API call to create new Artist
 */
const createArtist = (formData: ArtistFormFields): Promise<Artist> =>
  api(`/artists/create`, {
    method: "POST",
    body: JSON.stringify(withDefaults(formData)),
  });

/**
 * Custom hook to create new Artist and manage state
 */
const useCreateArtist = () =>
  useMutation<Artist, ApiError, ArtistFormFields>(createArtist);

/**
 * API call to update existing Artist
 */
const updateArtist = async (updates: Artist): Promise<Artist> =>
  api(`/artists/${updates.id}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });

/**
 * Custom hook to update existing Artist and manage state
 */
const useUpdateArtist = () => {
  const queryClient = useQueryClient();
  return useMutation<Artist, ApiError, Artist>(updateArtist, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["ARTIST", data.id]);
    },
  });
};

/**
 * Custom hook for create/edit Artist form
 */
const useArtistForm = (artist?: Artist) => {
  const { handleSubmit, ...rest } = useForm<ArtistFormFields>({
    defaultValues: artist,
  });

  // Run both create/edit hooks to create mutate functions and state management
  const { mutate: create, ...createMutation } = useCreateArtist();
  const { mutate: update, ...updateMutation } = useUpdateArtist();

  // If we've been given an existing Artist, then use update mutation. Otherwise, use create mutation.
  const isEdit = !!artist;
  const mutation = isEdit ? updateMutation : createMutation;

  // Create form submit handler that can handle either update or create mutate calls
  const onSubmit = handleSubmit(({ founderAttributes, ...formData }) => {
    const transformedData = {
      ...formData,
      // Fieldset of checkboxes with no selection have a value of `false`, convert to empty array
      founderAttributes: founderAttributes || [],
    };

    isEdit
      ? update({ ...artist, ...transformedData })
      : create(transformedData);
  });

  // Update image fields manually with uploaded image data
  const setUploadFields =
    (path: "heroImage" | "bannerImage") =>
    ({ url, file }: UploadedImage) => {
      rest.setValue(`${path}.url`, url);
      rest.setValue(`${path}.contentType`, file.type);
      rest.setValue(`${path}.importDate`, new Date().toISOString());
    };
  const setHeroUploadFields = setUploadFields("heroImage");
  const setBannerUploadFields = setUploadFields("bannerImage");

  // Given an index, create onSuccess handler for ArtistCollection image uploader
  const createCollectionUploadSetter =
    (index: number) =>
    ({ file, url }: UploadedImage) => {
      rest.setValue(`artistCollections.${index}.collectionImage`, {
        url,
        contentType: file.type,
        importDate: new Date().toISOString(),
      });
    };

  return {
    ...rest,
    isEdit,
    mutation,
    onSubmit,
    setHeroUploadFields,
    setBannerUploadFields,
    createCollectionUploadSetter,
  };
};

export { useCreateArtist, useArtistForm, useUpdateArtist };
